import React from "react";
import _ from "lodash"

export default class ComponentToPrint extends React.Component {
    render() {
        const searchData = this.props.searchData;
        const rows = _.cloneDeep(this.props.rows ?? []);
        const capturingPercentages = this.props.capturingPercentages ?? {};

        const getSafe = (value, fallback = "-") => {
            if (value === null || value === undefined) return fallback;
            if (typeof value === 'string' && value.trim() === '') return fallback;
            return value;
        }

        const normalizeJobType = (row) => {
            const raw = (row?.JobType ?? row?.jobTypeID ?? row?.jobType ?? row?.jobTypeName ?? row?.narration ?? row?.type ?? row?.jobTypeCode);
            if (raw === null || raw === undefined) return null;
            const rawNum = Number(raw);
            const rawStr = String(raw).toUpperCase();
            if (rawNum === 1 || rawStr.includes('GENERAL')) return 'general';
            if (rawNum === 2 || rawStr.includes('CASH')) return 'cash';
            if (rawNum === 3 || rawStr.includes('OT') || rawStr.includes('OVER')) return 'overtime';
            return null;
        }

        const normalizeMode = (row, rawOverride = undefined) => {
            const raw = (rawOverride ?? row?.VerificationMethod ?? row?.verificationMethod ?? row?.pluckingType ?? row?.mode ?? row?.captureMode ?? row?.biometricTypeID ?? row?.scanType ?? row?.modeID);
            if (raw === null || raw === undefined) return null;
            const rawNum = Number(raw);
            const rawStr = String(raw).toUpperCase();

            if (rawNum === 0 || rawNum === 6 || rawStr.includes('MANUAL')) return 'manual';
            if (rawNum === 3 || rawStr === 'QR') return 'qr';
            if (rawNum === 1 || rawStr.includes('FACE')) return 'face';

            if (rawNum === 2) return 'qr';
            return null;
        }

        const getCaptureKey = (row, modeRawOverride = undefined) => {
            const jobType = normalizeJobType(row);
            const mode = normalizeMode(row, modeRawOverride);
            if (!jobType || !mode) return null;
            return `${jobType}_${mode}`;
        }

        const parseToDate = (value) => {
            if (value === null || value === undefined) return null;
            if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

            const asString = String(value);
            const d = new Date(asString);
            return isNaN(d.getTime()) ? null : d;
        }

        const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

        const getTick = (isTick) => isTick
            ? <span style={{ color: "#2e7d32", fontWeight: "bold", fontSize: "16px", lineHeight: 1, display: "inline-block" }}>✔</span>
            : null;

        const getRowDate = (row) => {
            const val =
                (row?.StartTime ?? row?.startTime ?? row?.date ?? row?.searchDate ?? row?.createdDate ?? row?.workDate ?? row?.pluckingDate) ??
                (row?.EndTime ?? row?.endTime ?? row?.endDate ?? row?.EndDate ?? row?.endDateTime);
            return getSafe(val);
        }

        const getEmpNo = (row) => getSafe(row?.EmployeeCode ?? row?.employeeCode ?? row?.empNo ?? row?.epfNo ?? row?.employeeID ?? row?.empNumber);
        const getEmpName = (row) => getSafe(row?.FirstName ?? row?.firstName ?? row?.employeeName ?? row?.empName ?? row?.name);
        const getJobCode = (row) => getSafe(row?.JobCode ?? row?.jobCode ?? row?.jobCodeName ?? row?.jobTypeCode ?? row?.jobName);
        const getWorkedDivision = (row) => getSafe(
            row?.division ??
            row?.Division ??
            row?.divisionName ??
            row?.DivisionName ??
            row?.workedDivision ??
            row?.workedDivisionName ??
            row?.collectionPointName ??
            row?.collectionPoint
        );
        const getFieldName = (row) => getSafe(row?.FieldName ?? row?.fieldName ?? row?.field);
        const getOperator = (row) => getSafe(
            row?.operator ??
            row?.Operator ??
            row?.oparator ??
            row?.operatorName ??
            row?.OperatorName ??
            row?.oparatorName ??
            row?.createdBy ??
            row?.userName ??
            row?.createdUser
        );
        const getVerificationMethod = (row, kind) => {
            if (kind === 'completed') {
                return (
                    row?.EndVerificationMethod ??
                    row?.endVerificationMethod ??
                    row?.endVerificationMode ??
                    row?.endMode ??
                    row?.EndVerification ??
                    row?.endVerification
                );
            }

            return (
                row?.StartVerificationMethod ??
                row?.startVerificationMethod ??
                row?.startVerificationMode ??
                row?.startMode ??
                row?.StartVerification ??
                row?.startVerification ??
                row?.VerificationMethod ??
                row?.verificationMethod
            );
        }

        const hasCompletedEvent = (row) => {
            const endTimeVal = row?.EndTime ?? row?.endTime ?? row?.endDate ?? row?.EndDate ?? row?.endDateTime;
            return !!parseToDate(endTimeVal);
        }

        const getStartEndKeys = (row) => {
            const startMethod = getVerificationMethod(row, 'start');
            const startKey = getCaptureKey(row, startMethod);

            if (!hasCompletedEvent(row)) return { startKey, endKey: null };

            const endMethod = getVerificationMethod(row, 'completed');
            const endKey = getCaptureKey(row, endMethod);
            return { startKey, endKey };
        }

        const hasTick = (row, status, key) => {
            const method = getVerificationMethod(row, status);
            const mode = normalizeMode(row, method);
            if (!mode) return false;

            if (status === 'completed' && !hasCompletedEvent(row)) return false;

            const parts = key.split('_');
            const targetJobType = parts[0];
            const targetMode = parts[1];

            if (mode !== targetMode) return false;

            const baseJobType = normalizeJobType(row);

            return baseJobType === targetJobType;
        }

        const getFormattedRowDate = (row) => {
            const val =
                (row?.StartTime ?? row?.startTime ?? row?.date ?? row?.searchDate ?? row?.createdDate ?? row?.workDate ?? row?.pluckingDate) ??
                (row?.EndTime ?? row?.endTime ?? row?.endDate ?? row?.EndDate ?? row?.endDateTime);
            if (!val || val === '-') return { date: '-', time: '-' };

            try {
                const dateObj = new Date(val);
                if (isNaN(dateObj.getTime())) {
                    const parts = String(val).split(/[ T]/);
                    return {
                        date: parts[0] || val,
                        time: parts[1] || '-'
                    };
                }
                const yyyy = dateObj.getFullYear();
                const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
                const dd = String(dateObj.getDate()).padStart(2, '0');
                const dateStr = `${yyyy}-${mm}-${dd}`;

                let hours = dateObj.getHours();
                const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                const ampm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12;
                hours = hours ? hours : 12;
                const timeStr = `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;

                return { date: dateStr, time: timeStr };
            } catch (e) {
                return { date: val, time: '-' };
            }
        }

        return (
            <div>
                <h3><center><u>Worker Captured Audit Report - Sundry</u></center></h3>
                <div className="row pl-2 pb-4 pt-4">
                    <div className="col"><b>Group: </b> {searchData.group}</div>
                    <div className="col"><b>Operation Entity: </b> {searchData.estate}</div>
                    <div className="col"><b>Collection Point: </b> {searchData.division === undefined ? "All Collection Points" : searchData.division}</div>
                    <div className="col"><b>Field: </b> {searchData.feild === undefined ? "All Fields" : searchData.feild}</div>
                </div>
                <div className="row pl-2 pb-4">
                    <div className="col"><b>Mode: </b> {searchData.mode}</div>
                    <div className="col"><b>From Date: </b> {searchData.fromDate}</div>
                    <div className="col"><b>To Date: </b> {searchData.toDate}</div>
                </div>
                <div className="pl-2">
                    <div>
                        <table
                            id="summaryReportPdf">
                            <thead>
                                <tr style={{ fontSize: "12px" }}>
                                    <th rowSpan={2} style={{ fontSize: "12px" }}>Date</th>
                                    <th rowSpan={2} style={{ fontSize: "12px" }}>Emp No</th>
                                    <th rowSpan={2} style={{ fontSize: "12px" }}>Emp Name</th>
                                    <th rowSpan={2} style={{ fontSize: "12px" }}>Job Code</th>
                                    <th rowSpan={2} style={{ fontSize: "12px" }}>Collection Point</th>
                                    <th rowSpan={2} style={{ fontSize: "12px" }}>Field</th>
                                    <th rowSpan={2} style={{ fontSize: "12px" }}>Oparator</th>
                                    <th rowSpan={2} style={{ fontSize: "12px" }}>Status</th>
                                    <th colSpan={3} style={{ fontSize: "12px", textAlign: "center" }}>General</th>
                                    <th colSpan={3} style={{ fontSize: "12px", textAlign: "center" }}>Cash</th>
                                    <th colSpan={3} style={{ fontSize: "12px", textAlign: "center" }}>Over Time</th>
                                </tr>
                                <tr style={{ fontSize: "12px" }}>
                                    <th style={{ fontSize: "12px", textAlign: "center" }}>Manual</th>
                                    <th style={{ fontSize: "12px", textAlign: "center" }}>QR</th>
                                    <th style={{ fontSize: "12px", textAlign: "center" }}>Face ID</th>
                                    <th style={{ fontSize: "12px", textAlign: "center" }}>Manual</th>
                                    <th style={{ fontSize: "12px", textAlign: "center" }}>QR</th>
                                    <th style={{ fontSize: "12px", textAlign: "center" }}>Face ID</th>
                                    <th style={{ fontSize: "12px", textAlign: "center" }}>Manual</th>
                                    <th style={{ fontSize: "12px", textAlign: "center" }}>QR</th>
                                    <th style={{ fontSize: "12px", textAlign: "center" }}>Face ID</th>
                                </tr>
                            </thead>
                            <tbody>                                 {(rows ?? []).map((r, idx) => {
                                     const formattedDate = getFormattedRowDate(r);
                                     return (
                                         <React.Fragment key={idx}>
                                             <tr style={{ fontSize: "12px" }}>
                                                 <td style={{ fontSize: "12px", verticalAlign: "middle" }} rowSpan={2}>
                                                     <div>{formattedDate.date}</div>
                                                     <div style={{ fontSize: '11px', color: '#555' }}>{formattedDate.time}</div>
                                                 </td>
                                                 <td style={{ fontSize: "12px", verticalAlign: "middle" }} rowSpan={2}>{getEmpNo(r)}</td>
                                                 <td style={{ fontSize: "12px", verticalAlign: "middle" }} rowSpan={2}>{getEmpName(r)}</td>
                                                 <td style={{ fontSize: "12px", verticalAlign: "middle" }} rowSpan={2}>{getJobCode(r)}</td>
                                                 <td style={{ fontSize: "12px", verticalAlign: "middle" }} rowSpan={2}>{getWorkedDivision(r)}</td>
                                                 <td style={{ fontSize: "12px", verticalAlign: "middle" }} rowSpan={2}>{getFieldName(r)}</td>
                                                 <td style={{ fontSize: "12px", verticalAlign: "middle" }} rowSpan={2}>{getOperator(r)}</td>
                                                 <td style={{ fontSize: "12px" }}>Start</td>

                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'start', 'general_manual'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'start', 'general_qr'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'start', 'general_face'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'start', 'cash_manual'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'start', 'cash_qr'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'start', 'cash_face'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'start', 'overtime_manual'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'start', 'overtime_qr'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'start', 'overtime_face'))}</td>
                                             </tr>
                                             <tr style={{ fontSize: "12px" }}>
                                                 <td style={{ fontSize: "12px" }}>Completed</td>

                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'completed', 'general_manual'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'completed', 'general_qr'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'completed', 'general_face'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'completed', 'cash_manual'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'completed', 'cash_qr'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'completed', 'cash_face'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'completed', 'overtime_manual'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'completed', 'overtime_qr'))}</td>
                                                 <td style={{ fontSize: "12px", textAlign: "center" }}>{getTick(hasTick(r, 'completed', 'overtime_face'))}</td>
                                             </tr>
                                         </React.Fragment>
                                     )
                                 })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th style={{ textAlign: "center", fontWeight: "bold" }} colSpan={8}>Capturing Percentages</th>
                                    <th style={{ textAlign: "center" }}>{capturingPercentages.general_manual}%</th>
                                    <th style={{ textAlign: "center" }}>{capturingPercentages.general_qr}%</th>
                                    <th style={{ textAlign: "center" }}>{capturingPercentages.general_face}%</th>
                                    <th style={{ textAlign: "center" }}>{capturingPercentages.cash_manual}%</th>
                                    <th style={{ textAlign: "center" }}>{capturingPercentages.cash_qr}%</th>
                                    <th style={{ textAlign: "center" }}>{capturingPercentages.cash_face}%</th>
                                    <th style={{ textAlign: "center" }}>{capturingPercentages.overtime_manual}%</th>
                                    <th style={{ textAlign: "center" }}>{capturingPercentages.overtime_qr}%</th>
                                    <th style={{ textAlign: "center" }}>{capturingPercentages.overtime_face}%</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    }
}